// DraggableCard wrapper
import React, { useState, useRef, useEffect } from "react";

interface DraggableCardProps {
    children: any;
    initialX?: number;
    initialY?: number;
    initialRotation?: number;
    dropCallabck?: Function;
    hoverCallback?: Function;
}

export default function DraggableCard({
    children,
    initialX = 0,
    initialY = 0,
    initialRotation = 0,
    dropCallabck = () => { },
    hoverCallback = () => { }
}: DraggableCardProps) {
    const [pos, setPos] = useState({ x: initialX, y: initialY });
    const [rotation, setRotation] = useState(initialRotation);
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

    useEffect(() => {
        if (!isDragging) return;

        const handleGlobalMouseMove = (e: MouseEvent) => {
            hoverCallback(-1);

            setPos({
                x: dragStart.current.posX + (e.clientX - dragStart.current.x),
                y: dragStart.current.posY + (e.clientY - dragStart.current.y),
            });
        };

        const handleGlobalMouseUp = (e: MouseEvent) => {
            const finalPos = {
                x: dragStart.current.posX + (e.clientX - dragStart.current.x),
                y: dragStart.current.posY + (e.clientY - dragStart.current.y),
            };
            dropCallabck(finalPos);
            setPos(finalPos); // or keep separate if you need the reset effect
            setIsDragging(false);
        };

        window.addEventListener("mousemove", handleGlobalMouseMove);
        window.addEventListener("mouseup", handleGlobalMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleGlobalMouseMove);
            window.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [isDragging]); // removed pos.y, initialX/Y from deps - they weren't helping

    // Reset to home position when drag ends or initial values change
    useEffect(() => {
        if (!isDragging) {
            setPos({ x: initialX, y: initialY });
            setRotation(initialRotation);
        }
    }, [isDragging, initialX, initialY, initialRotation]);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            posX: pos.x,
            posY: pos.y,
        };
        setRotation(0);
        hoverCallback(-1);
    };

    const handleMouseEnter = () => {
        if (!isDragging) {
            hoverCallback()
            setPos({ x: initialX, y: initialY });
        }
    };

    const handleMouseLeave = () => {
        if (!isDragging) {
            hoverCallback(-1);
        }
    };

    return (
        <div
            style={{
                position: "absolute",
                left: pos.x,
                top: pos.y,
                transform: `rotate(${rotation}deg)`,
                cursor: isDragging ? "grabbing" : "grab",
                userSelect: "none",
            }}
        >
            <div
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    position: "relative",
                }}
            >
                {children}
            </div>
        </div>
    );
}